import React, { useState } from 'react';
import { Alert, Card, Stack, Button, Collapse } from 'react-bootstrap';
import { ExclamationTriangleFill, ClockFill, ExclamationCircleFill, ChevronDown, ChevronUp, Save } from 'react-bootstrap-icons';
import DateRange from "../../components/Daterange";
import { useDataGeneration, STATUS_CANCELLED, STATUS_TIMEOUT, STATUS_ERROR } from '../../api/generate/store'; // Import types
import HistoryContainer from '../../containers/HistoryContainer';
import { ERROR_MESSAGES, DEFAULT_ERROR } from '../../constants';
import BasePage from '../../components/BasePage';

const NewPage = ({ onGenerate = (date: string) => {}, loading = false }) => {
    const {
        status,
        generationDuration,
        errorReason,
        technicalError // Add technical error details
    } = useDataGeneration(state => state); // Added type annotation for state
    
    //const { settingsData: settings } = useSettings((state) => state);

    // State for technical details collapse
    const [technicalDetailsOpen, setTechnicalDetailsOpen] = useState(false);

    // Helper to format duration
    const formatDuration = (seconds: number) => {
        if (seconds < 60) {
            return `${seconds.toFixed(1)} seconds`;
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes} minute${minutes > 1 ? 's' : ''} ${remainingSeconds.toFixed(0)} seconds`;
    };

    return (
        <BasePage>
        <Stack gap={0}>            
            {/* Status notification for cancelled, timeout, or error */}
            {(status === STATUS_CANCELLED || status === STATUS_TIMEOUT || status === STATUS_ERROR) && generationDuration > 0 && (
                <Alert 
                    variant={status === STATUS_CANCELLED ? "warning" : "danger"} 
                    className="d-flex flex-column"
                >
                    <div className="d-flex align-items-start">
                        {status === STATUS_CANCELLED ? (
                            <>
                                <ExclamationTriangleFill className="me-2 mt-1" />
                                <div className="flex-grow-1">
                                    <div>Last generation was <strong>cancelled</strong> after {formatDuration(generationDuration)}</div>
                                    {errorReason && <small className="text-muted">{errorReason}</small>}
                                </div>
                            </>
                        ) : status === STATUS_TIMEOUT ? (
                            <>
                                <ClockFill className="me-2 mt-1" />
                                <div className="flex-grow-1">
                                    <div>Last generation <strong>timed out</strong> after {formatDuration(generationDuration)}</div>
                                    <div className="mt-1">Please try again or contact support if this persists.</div>
                                </div>
                            </>
                        ) : (
                            <>
                                <ExclamationCircleFill className="me-2 mt-1" />
                                <div className="flex-grow-1">
                                    {/* Show user-friendly message based on status code */}
                                    {technicalError && technicalError.statusCode ? (
                                        <div>
                                            <div><strong>{ERROR_MESSAGES[technicalError.statusCode]?.message || DEFAULT_ERROR.message}</strong></div>
                                            <div className="mt-1">{ERROR_MESSAGES[technicalError.statusCode]?.action || DEFAULT_ERROR.action}</div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div>Last generation <strong>failed</strong> after {formatDuration(generationDuration)}</div>
                                            {errorReason && <div className="mt-1">{errorReason}</div>}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    
                    {/* Technical details section for ERROR status */}
                    {status === STATUS_ERROR && technicalError && (
                        <div className="mt-3">
                            <Button
                                variant="link"
                                size="sm"
                                onClick={() => setTechnicalDetailsOpen(!technicalDetailsOpen)}
                                aria-controls="technical-details"
                                aria-expanded={technicalDetailsOpen}
                                className="p-0 text-decoration-none d-flex align-items-center"
                            >
                                {technicalDetailsOpen ? <ChevronUp className="me-1" /> : <ChevronDown className="me-1" />}
                                More Info
                            </Button>
                            <Collapse in={technicalDetailsOpen}>
                                <div id="technical-details" className="mt-2">
                                    <Card bg="transparent" className="border-0">
                                        <Card.Body className="small">
                                            <div><strong>Status Code:</strong> {technicalError.statusCode || 'N/A'}</div>
                                            <div><strong>Endpoint:</strong> {technicalError.endpoint || 'N/A'}</div>
                                            <div><strong>Response Time:</strong> {technicalError.duration ? `${technicalError.duration.toFixed(2)}s` : 'N/A'}</div>
                                            <div><strong>Timestamp:</strong> {technicalError.timestamp ? new Date(technicalError.timestamp).toLocaleString() : 'N/A'}</div>
                                            {technicalError.rawMessage && (
                                                <div className="mt-2">
                                                    <strong>Error Message:</strong>
                                                    <div className="text-monospace mt-1" style={{ wordBreak: 'break-word' }}>
                                                        {technicalError.rawMessage}
                                                    </div>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </div>
                            </Collapse>
                        </div>
                    )}
                </Alert>
            )}

            {/* Section for initiating new generation */}
            <DateRange onClick={onGenerate} loading={loading} />

            {/* Section for viewing previously generated files */}
            <HistoryContainer />
        </Stack>
        </BasePage>
    );
};

export default NewPage;
